import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import api from "@/lib/api";
import { EventCard } from "@/components/EventCard";

export const SportsBirthYear = ({ items, year, compact = false, className = "" }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="sports-birth-year" className={`rounded-2xl border border-ink bg-paper ${compact ? "p-4" : "p-5"} ${className}`}>
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-gold" />
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">The sporting year you were born{year ? ` · ${year}` : ""}</p>
      </div>
      <ul className={`mt-3 ${compact ? "space-y-1 max-h-48 overflow-auto pr-1" : "space-y-1.5"}`}>
        {items.map((s) => (
          <li key={s.id} data-testid={`sports-birth-${s.id}`} className="flex items-start gap-2 text-[13px]">
            <span className="w-[120px] shrink-0 text-[10px] font-bold uppercase tracking-wider text-gold pt-0.5">{s.competition}</span>
            <span className="min-w-0 flex-1 text-ink truncate" title={s.detail}>{s.title}</span>
          </li>
        ))}
      </ul>
      <Link to={`/sports${year ? `?year=${year}` : ""}`} data-testid="sports-birth-link" className="mt-3 inline-block text-[12px] font-semibold text-gold hover:underline">Full sporting almanac →</Link>
    </div>
  );
};

export const SportsSection = ({ events = [], dateLabel = "today" }) => {
  const [cols, setCols] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { api.get("/sports/decades", { params: { offsets: "10,25,50" } }).then((r) => setCols(r.data.items)).catch(() => setCols([])); }, []);
  if ((!cols || cols.length === 0) && events.length === 0) return null;
  const INITIAL = 6;
  const shown = expanded ? events : events.slice(0, INITIAL);
  return (
    <section id="sports" data-testid="sports-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-gold" />
          <h2 className="text-2xl font-bold text-ink">Sport on this day</h2>
          <span data-testid="sports-day-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{events.length}</span>
        </div>
        <p className="text-[13px] text-ink-muted">Finals, medals and records decided on {dateLabel}.</p>
      </div>
      {events.length === 0 ? (
        <p data-testid="sports-day-empty" className="text-sm text-ink-muted">No recorded sporting finals on this date yet.</p>
      ) : (
        <>
          <div data-testid="sports-day-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {shown.map((e, i) => <EventCard key={e.id} e={e} i={i} total={shown.length} />)}
          </div>
          {events.length > INITIAL && (
            <button type="button" data-testid="sports-day-toggle" onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center px-5 h-10 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white transition-colors">
              {expanded ? "Show fewer" : `Show all sport (${events.length - INITIAL} more)`}
            </button>
          )}
        </>
      )}
      {cols && cols.length > 0 && (
      <div className="mt-6 rounded-2xl border border-ink bg-paper px-6 py-7 md:px-10 md:py-9">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-gold" />
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Sporting almanac</p>
            </div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink leading-tight">Who was champion, 10, 25 and 50 years ago</h2>
            <p className="mt-1 text-[14px] text-ink-muted">World Cups, Olympics, Wimbledon, the Masters, Super Bowl and more — year by year since 1877.</p>
          </div>
          <div className="flex flex-wrap gap-2 self-start md:self-auto">
            <Link to="/quiz" data-testid="sports-quiz-link" className="inline-flex items-center px-5 h-11 rounded-full border border-gold text-gold text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Play the History Quiz</Link>
            <Link to="/sports" data-testid="sports-all-link" className="inline-flex items-center px-5 h-11 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Browse every year →</Link>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {cols.map((c) => (
            <div key={c.year} data-testid={`sports-year-${c.year}`} className="bg-white border border-[#e5e7eb] rounded-xl p-4">
              <p className="text-[22px] font-bold text-ink leading-none">{c.year} <span className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider">{c.years_ago} yrs ago</span></p>
              <ul className="mt-3 space-y-1.5">
                {c.items.map((s) => (
                  <li key={s.id} className="text-[13px] text-ink leading-snug"><span className="text-[10px] font-bold uppercase tracking-wider text-gold mr-1.5">{s.competition}</span>{s.title}</li>
                ))}
              </ul>
              {c.total > c.items.length && <Link to={`/sports?year=${c.year}`} className="mt-2 inline-block text-[12px] font-semibold text-gold hover:underline">+{c.total - c.items.length} more →</Link>}
            </div>
          ))}
        </div>
      </div>
      )}
    </section>
  );
};
